export type ProductId = "idv" | "poa" | "bank" | "affordability" | "aml" | "card";

export type Limits = { fail: number; review: number };

export type DataMatchRule = {
  active: boolean;
  service: string[];
  limits: Limits;
};

export type CheckOption = {
  documents: string[];
  providers: string[];
};

export type CheckConfig = {
  provider: string;
  manual: boolean;
  checkoptions: CheckOption[];
  settings: {
    persondatamatch: boolean;
    addressdatamatch: boolean;
    continueonfail: boolean;
    continueonreview: boolean;
    continueon: string[];
    retryonfail: number;
    retryonreview: number;
    datamatchlimits: Limits;
    datamatch: Record<string, DataMatchRule>;
    validation: Record<string, { active: boolean }>;
    /** Credit-card BIN / last-digit capture (card product only). */
    cardFirstDigits?: number;
    cardLastDigits?: number;
    /** Enable Stripe-backed credit card verification (card product only). */
    cardStripeVerification?: boolean;
  };
  flags: Record<string, boolean>;
  promptconfig: Record<string, string>;
};

export type NotificationConfig = {
  url: string;
  authtype: string;
  username: string;
  password: string;
  send400: boolean;
  retryon500: boolean;
};

export const DATAMATCH_FIELDS: { key: string; label: string; group: string }[] = [
  { key: "matchname", label: "Full name", group: "Identity" },
  { key: "matchfirstname", label: "First name", group: "Identity" },
  { key: "matchlastname", label: "Last name", group: "Identity" },
  { key: "matchdob", label: "Date of birth", group: "Identity" },
  { key: "matchaddress", label: "Address (full)", group: "Address" },
  { key: "matchaddressstreet", label: "Street", group: "Address" },
  { key: "matchaddresshousenumber", label: "House number", group: "Address" },
  { key: "matchaddresspostcode", label: "Postcode", group: "Address" },
  { key: "matchaddresscity", label: "City", group: "Address" },
  { key: "matchaddresscountry", label: "Country", group: "Address" },
];

export const SERVICE_OPTIONS = ["internal.1", "internal.2", "external.1"];

export const SERVICE_LABELS: Record<string, string> = {
  "internal.1": "Levenshtein",
  "internal.2": "Ignore middle names",
  "external.1": "Name API",
};

export function sanitizeServiceOptions(services: string[]): string[] {
  return services.filter((s) =>
    (SERVICE_OPTIONS as readonly string[]).includes(s),
  );
}

export const CONTINUE_ON_OPTIONS = [
  "process-needs_review",
  "datamatch-needs_review-name",
  "datamatch-needs_review-dob",
  "datamatch-needs_review-city",
  "datamatch-needs_review-street",
  "datamatch-needs_review-postcode",
  "datamatch-needs_review-country",
  "validation-needs_review-documentdate",
  "validation-needs_review-expirydate",
];

function rule(active = false, service: string[] = [], fail = 0, review = 75): DataMatchRule {
  return { active, service, limits: { fail, review } };
}

function defaultDataMatch(): Record<string, DataMatchRule> {
  return {
    matchname: rule(false, ["external.1"], -1, 71),
    matchfirstname: rule(false, ["external.1", "internal.1"], 0, 75),
    matchlastname: rule(false, ["external.1", "internal.1"], 0, 75),
    matchdob: rule(false, [], 0, 99),
    matchaddress: rule(false, ["internal.1"], 0, 59),
    matchaddressstreet: rule(false, ["internal.1"], -1, 79),
    matchaddresshousenumber: rule(false, ["internal.1"], -1, -1),
    matchaddresspostcode: rule(false, ["internal.1"], -1, 99),
    matchaddresscity: rule(false, ["internal.1"], -1, 79),
    matchaddresscountry: rule(false, ["internal.1"], -1, -1),
  };
}

export type ProductSchema = {
  providers: string[];
  documents: string[];
  defaultDocuments: string[];
  providerRouting: string[];
  validation: { key: string; label: string; hint: string; on: boolean }[];
  flags: { key: string; label: string; hint: string; on: boolean }[];
  prompts: { key: string; label: string; hint: string }[];
};

export const PRODUCT_SCHEMA: Record<ProductId, ProductSchema> = {
  idv: {
    providers: ["incode", "sonio", "mixed"],
    documents: ["ID", "PASSPORT", "RESIDENCE", "DRIVINGLICENSE"],
    defaultDocuments: ["ID", "PASSPORT"],
    providerRouting: ["incode"],
    validation: [
      { key: "validateName", label: "Validate name", hint: "Name present and parsable", on: true },
      { key: "validateDocumentType", label: "Validate document type", hint: "Type is in the accepted list", on: true },
      { key: "validateExpiryDate", label: "Validate expiry date", hint: "Document not expired", on: true },
      { key: "validateFaceMatch", label: "Validate face match", hint: "Selfie matches document portrait", on: true },
      { key: "validateLiveness", label: "Validate liveness", hint: "Anti-spoofing challenge passed", on: true },
    ],
    flags: [
      { key: "use_nfc_when_available", label: "use_nfc_when_available", hint: "Read the chip on ePassports / eIDs", on: false },
      { key: "idv_sonio_fallback_staple", label: "idv_sonio_fallback_staple", hint: "Fall back to Sonio extraction on provider failure", on: true },
      { key: "use_accepted_documents_validation", label: "use_accepted_documents_validation", hint: "Hard-fail documents outside the accepted list", on: true },
    ],
    prompts: [{ key: "idvPrompt", label: "idvPrompt", hint: "Extraction / classification prompt used by the IDV engine." }],
  },
  poa: {
    providers: ["mixed", "sonio", "incode"],
    documents: ["INVOICE", "BILL", "BANK", "LEASE", "OFFICIAL", "PAYSLIP", "INSURANCE"],
    defaultDocuments: ["INVOICE", "BILL", "BANK"],
    providerRouting: ["sonio", "incode", "incode+sonio"],
    validation: [
      { key: "validateAddress", label: "Validate address", hint: "A postal address was extracted", on: true },
      { key: "validateName", label: "Validate name", hint: "Recipient name was extracted", on: true },
      { key: "validateDocumentType", label: "Validate document type", hint: "Type is in the accepted list", on: true },
      { key: "validateDocumentDate", label: "Validate document date", hint: "Issue date within max age", on: true },
      { key: "validateExpiryDate", label: "Validate expiry date", hint: "Document not expired", on: true },
    ],
    flags: [
      { key: "upload_poa_to_incode", label: "upload_poa_to_incode", hint: "Forward the document to Incode", on: true },
      { key: "poa_sonio_fallback_staple", label: "poa_sonio_fallback_staple", hint: "Fall back to Sonio extraction", on: true },
      { key: "use_accepted_documents_validation", label: "use_accepted_documents_validation", hint: "Hard-fail documents outside the accepted list", on: true },
    ],
    prompts: [{ key: "poaPrompt", label: "poaPrompt", hint: "Document classification and data-extraction prompt." }],
  },
  bank: {
    providers: ["mixed", "tink", "plaid", "truelayer", "yapily"],
    documents: ["OPENBANKING", "BANK", "IBANCHECK"],
    defaultDocuments: ["OPENBANKING"],
    providerRouting: ["tink", "plaid", "sonio", "tink+sonio"],
    validation: [
      { key: "validateAccountHolder", label: "Validate account holder", hint: "Holder name returned by the bank", on: true },
      { key: "validateIban", label: "Validate IBAN", hint: "IBAN checksum and country", on: true },
      { key: "validateName", label: "Validate name", hint: "Name present and parsable", on: true },
      { key: "validateAddress", label: "Validate address", hint: "Address returned by the bank", on: false },
    ],
    flags: [
      { key: "allow_manual_iban_entry", label: "allow_manual_iban_entry", hint: "Let the user type the IBAN if the bank link fails", on: false },
      { key: "store_transactions", label: "store_transactions", hint: "Persist 90 days of transactions", on: false },
    ],
    prompts: [{ key: "bankPrompt", label: "bankPrompt", hint: "Prompt used when a statement is parsed instead of Open Banking." }],
  },
  affordability: {
    providers: ["mixed", "sonio", "tink"],
    documents: ["BANK", "PAYSLIP", "TAX"],
    defaultDocuments: ["BANK", "PAYSLIP"],
    providerRouting: ["sonio", "tink", "tink+sonio"],
    validation: [
      { key: "validateIncome", label: "Validate income", hint: "At least one recurring income stream found", on: true },
      { key: "validateName", label: "Validate name", hint: "Statement holder name matches", on: true },
      { key: "validateDocumentDate", label: "Validate document date", hint: "Statement within max age", on: true },
      { key: "validateAffordabilityThreshold", label: "Validate threshold", hint: "Disposable income above configured limit", on: true },
    ],
    flags: [
      { key: "use_net_income", label: "use_net_income", hint: "Score on net rather than gross income", on: true },
      { key: "include_gambling_spend", label: "include_gambling_spend", hint: "Count gambling spend as expense", on: true },
    ],
    prompts: [{ key: "affordabilityPrompt", label: "affordabilityPrompt", hint: "Income / expense categorisation prompt." }],
  },
  aml: {
    providers: ["mixed", "comply", "dowjones", "sonio"],
    documents: ["PEP", "SANCTIONS", "ADVERSEMEDIA", "WATCHLIST"],
    defaultDocuments: ["PEP", "SANCTIONS"],
    providerRouting: ["comply", "dowjones", "comply+sonio"],
    validation: [
      { key: "validateName", label: "Validate name", hint: "Name available for screening", on: true },
      { key: "validateDob", label: "Validate date of birth", hint: "DOB available to disambiguate hits", on: true },
      { key: "validateNoSanctionHit", label: "Validate no sanction hit", hint: "Fail on any sanctions match", on: true },
    ],
    flags: [
      { key: "ongoing_monitoring", label: "ongoing_monitoring", hint: "Re-screen the customer daily", on: true },
      { key: "auto_discard_low_score", label: "auto_discard_low_score", hint: "Discard hits below the review limit", on: false },
    ],
    prompts: [{ key: "amlPrompt", label: "amlPrompt", hint: "Adverse-media relevance-scoring prompt." }],
  },
  card: {
    providers: ["sonio"],
    documents: ["CREDITCARD"],
    defaultDocuments: ["CREDITCARD"],
    providerRouting: ["sonio"],
    validation: [
      { key: "validateCardNumber", label: "Validate card number", hint: "Digits extracted and checksum consistent", on: true },
      { key: "validateCardHolder", label: "Validate card holder", hint: "Owner name extracted from the card", on: true },
      { key: "validateCardCategory", label: "Validate card category", hint: "Card classified as business or private", on: true },
      { key: "validateExpiryDate", label: "Validate expiry date", hint: "Card not expired", on: true },
    ],
    flags: [],
    prompts: [],
  },
};

export function defaultCheckConfig(id: ProductId): CheckConfig {
  const schema = PRODUCT_SCHEMA[id];
  return {
    provider: schema.providers[0],
    manual: false,
    checkoptions: [
      {
        documents: schema.defaultDocuments,
        providers: id === "idv" ? ["incode"] : [schema.providerRouting[0]],
      },
    ],
    settings: {
      persondatamatch: id !== "card",
      addressdatamatch: id === "poa",
      continueonfail: false,
      continueonreview: false,
      continueon:
        id === "poa"
          ? [
              "process-needs_review",
              "datamatch-needs_review-city",
              "datamatch-needs_review-street",
              "datamatch-needs_review-postcode",
            ]
          : [],
      retryonfail: 2,
      retryonreview: 2,
      datamatchlimits: { fail: 0, review: 75 },
      datamatch: defaultDataMatch(),
      validation: Object.fromEntries(
        schema.validation.map((v) => [v.key, { active: v.on }]),
      ),
      ...(id === "card"
        ? {
            cardFirstDigits: 6,
            cardLastDigits: 4,
            cardStripeVerification: false,
          }
        : {}),
    },
    flags: Object.fromEntries(schema.flags.map((f) => [f.key, f.on])),
    promptconfig: Object.fromEntries(schema.prompts.map((p) => [p.key, ""])),
  };
}
